/**
 * Port of entrypoint.sh:494-519 (`configure_graphify_env`). A locally
 * published graph lives in the shared GCS-FUSE user-home bucket at a fixed
 * mount point, under `users/_shared/graphify/<repo>/latest` — this computes
 * the env var contract the rest of boot (and the agent process) consumes,
 * without touching the filesystem itself (that's `dirExists`, injected so
 * this stays pure/testable).
 */
const USER_HOME_MOUNT = "/mnt/conveyor-users";

export interface GraphifyEnvInput {
  CONVEYOR_GRAPHIFY_DISABLE?: string;
  CONVEYOR_GRAPHIFY_SLUG?: string;
  CONVEYOR_GRAPHIFY_SHARED_ROOT?: string;
  CONVEYOR_GRAPHIFY_DIR?: string;
  CONVEYOR_GRAPHIFY_GRAPH?: string;
}

export function computeGraphifyEnv(
  env: GraphifyEnvInput,
  repoName: string | undefined,
  dirExists: (p: string) => boolean,
): Record<string, string> {
  if (env.CONVEYOR_GRAPHIFY_DISABLE === "1") {
    return {};
  }

  const slug = env.CONVEYOR_GRAPHIFY_SLUG || repoName;
  if (!slug) {
    return {};
  }

  const primaryRoot = `${USER_HOME_MOUNT}/users/_shared/graphify`;
  const legacyRoot = `${USER_HOME_MOUNT}/_shared/graphify`;
  let sharedRoot = env.CONVEYOR_GRAPHIFY_SHARED_ROOT;
  if (!sharedRoot) {
    // Primary root wins whenever it exists (or legacy doesn't) — legacy is
    // only chosen when primary is missing AND legacy is actually present.
    sharedRoot = dirExists(primaryRoot) || !dirExists(legacyRoot) ? primaryRoot : legacyRoot;
  }

  const dir = env.CONVEYOR_GRAPHIFY_DIR || `${sharedRoot}/${slug}/latest`;
  const graph = env.CONVEYOR_GRAPHIFY_GRAPH || `${dir}/graph.json`;

  return {
    CONVEYOR_GRAPHIFY_SLUG: slug,
    CONVEYOR_GRAPHIFY_SHARED_ROOT: sharedRoot,
    CONVEYOR_GRAPHIFY_DIR: dir,
    CONVEYOR_GRAPHIFY_GRAPH: graph,
  };
}
